import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { communityService } from '../services/communityService';
import { WalkingBuddyMatch, WalkingBuddyProfile } from '../types/community';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import {
  Users,
  MapPin,
  Sparkles,
  MessageCircle,
  Loader2,
  X,
} from 'lucide-react';
import { cn } from './ui/utils';
import { toast } from 'sonner';
import { useApp } from '../context/PetParksContext';
import { translations } from '../data/translations';

interface WalkingBuddyPanelProps {
  onOpenModal?: () => void;
  onCloseModal?: () => void;
  showCloseButton?: boolean;
}

export const WalkingBuddyPanel: React.FC<WalkingBuddyPanelProps> = ({
  onOpenModal,
  onCloseModal,
  showCloseButton = false,
}) => {
  const navigate = useNavigate();
  const { t } = useApp();
  const [isActive, setIsActive] = useState(false);
  const [matches, setMatches] = useState<WalkingBuddyMatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Profile settings
  const [availability, setAvailability] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: true,
    sunday: true,
  });
  const [preferredTime, setPreferredTime] = useState<
    'morning' | 'afternoon' | 'evening' | 'flexible'
  >('flexible');

  useEffect(() => {
    if (isActive) {
      loadMatches();
    }
  }, [isActive]);

  const loadMatches = async () => {
    setIsLoading(true);
    try {
      const matchResults =
        await communityService.getWalkingBuddyMatches('current-user-id');
      setMatches(matchResults);
    } catch (error) {
      toast.error(t('failedToLoadMatches'));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async (nextActive: boolean) => {
    setIsSavingProfile(true);
    try {
      const profile: WalkingBuddyProfile = {
        user_id: 'current-user-id',
        is_active: nextActive,
        availability,
        preferred_time: preferredTime,
        preferred_parks: [],
        pet_size_preference: ['small', 'medium', 'large'],
        pet_energy_preference: ['low', 'medium', 'high'],
        distance_willing: 5,
      };

      await communityService.updateWalkingBuddyProfile(profile);
      setIsActive(nextActive);

      if (nextActive) {
        toast.success(t('walkingBuddyActive'));
        loadMatches();
      } else {
        toast.success(t('walkingBuddyDeactivated'));
        setMatches([]);
      }
    } catch (error) {
      toast.error(
        nextActive
          ? t('failedToActivateWalkingBuddy')
          : t('failedToSaveChanges')
      );
      console.error(error);
    } finally {
      setIsSavingProfile(false);
      setShowSetup(false);
      // Close modal after saving if it was opened via modal
      if (onCloseModal && nextActive) {
        // Small delay to show success message
        setTimeout(() => {
          onCloseModal();
        }, 500);
      }
    }
  };

  const handleToggleActive = (enabled: boolean) => {
    // When turning ON, take the user through setup first.
    if (enabled && !isActive) {
      // If onOpenModal is provided, open the modal instead of showing inline setup
      if (onOpenModal) {
        onOpenModal();
        setShowSetup(true);
      } else {
        setShowSetup(true);
      }
      return;
    }
    // Turning OFF happens immediately.
    void saveProfile(false);
  };

  const handleStartChat = async (match: WalkingBuddyMatch) => {
    try {
      await communityService.startConversation([
        'current-user-id',
        match.user_id,
      ]);
      toast.success(t('openingSocialToChat'));
      // Open Petso Social → Messages with this user (walking buddy)
      navigate({
        to: '/dashboard/social',
        search: { openChat: match.username },
      });
    } catch (error) {
      toast.error(t('failedToStartChat'));
      console.error(error);
    }
  };

  if (showSetup) {
    const isEditing = isActive;
    return (
      <Card className="p-4 sm:p-6 min-w-0">
        <div className="space-y-4">
          <div>
            <h3 className="mb-1 font-semibold">
              {isEditing
                ? t('editWalkingBuddyPreferences')
                : t('walkingBuddySetup')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isEditing
                ? t('updateAvailabilityPreferredTime')
                : t('findCompatiblePartnersInArea')}
            </p>
          </div>

          <Separator />

          {/* Availability */}
          <div className="space-y-3">
            <Label>{t('whenAvailable')}</Label>
            <div className="grid gap-2">
              {Object.entries(availability).map(([day, checked]) => (
                <div key={day} className="flex items-center space-x-2">
                  <Checkbox
                    id={day}
                    checked={checked}
                    onCheckedChange={(value) =>
                      setAvailability((prev) => ({
                        ...prev,
                        [day]: value as boolean,
                      }))
                    }
                  />
                  <Label
                    htmlFor={day}
                    className="cursor-pointer font-normal capitalize"
                  >
                    {t(day as keyof typeof translations.en)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Preferred Time */}
          <div className="space-y-3">
            <Label>{t('preferredTimeOfDay')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {(['morning', 'afternoon', 'evening', 'flexible'] as const).map(
                (time) => (
                  <Button
                    key={time}
                    variant={preferredTime === time ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreferredTime(time)}
                    className="capitalize min-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-2 text-xs cursor-pointer"
                  >
                    {t(time)}
                  </Button>
                )
              )}
            </div>
          </div>

          <Separator />

          {/* Privacy Notice */}
          <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <span className="font-medium">{t('privacy')}:</span>{' '}
              {t('privacyNoticeWalkingBuddy')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSetup(false)}
              className="w-full cursor-pointer"
              disabled={isSavingProfile}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                void saveProfile(true);
              }}
              className="w-full cursor-pointer"
              disabled={isSavingProfile}
            >
              {isSavingProfile ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('saving')}
                </>
              ) : isEditing ? (
                t('savePreferences')
              ) : (
                <>
                  <Users className="mr-2 h-4 w-4" />
                  {t('activateFindMatches')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6 min-w-0">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="mb-1 font-semibold">{t('walkingBuddy')}</h3>
            <p className="text-sm text-muted-foreground truncate">
              {t('findCompatiblePartners')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={handleToggleActive} />
            {showCloseButton && onCloseModal && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onCloseModal}
                className="h-8 w-8 cursor-pointer"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">{t('close')}</span>
              </Button>
            )}
          </div>
        </div>

        {isActive && (
          <>
            <Separator />

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : matches.length === 0 ? (
              <div className="py-8 text-center">
                <Users className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                <p className="mb-1 font-medium">{t('noMatchesYet')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('checkBackLaterForMatches')}
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3 pr-4">
                  {matches.map((match) => (
                    <Card key={match.user_id} className="p-3">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                          {/* User Info */}
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                            <Users className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">{match.username}</p>
                            <p className="text-sm text-muted-foreground">
                              {match.pet_name} •{' '}
                              {t(
                                match.pet_size as keyof typeof translations.en
                              )}{' '}
                              •{' '}
                              {t(
                                match.pet_energy as keyof typeof translations.en
                              )}{' '}
                              {t('energy')}
                            </p>
                          </div>
                        </div>

                        {/* Compatibility Score */}
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="default"
                            className={cn(
                              'text-xs',
                              match.compatibility_score >= 90
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : match.compatibility_score >= 75
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : ''
                            )}
                          >
                            <Sparkles className="mr-1 h-3 w-3" />
                            {match.compatibility_score}% {t('matchLabel')}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            <MapPin className="mr-1 h-3 w-3" />
                            {match.distance_km} {t('kmAway')}
                          </Badge>
                        </div>

                        {/* Common Parks */}
                        {match.common_parks.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {match.common_parks.length === 1
                              ? `1 ${t('parkInCommon')}`
                              : `${match.common_parks.length} ${t('parksInCommon')}`}
                          </p>
                        )}

                        {/* Action Button */}
                        <Button
                          size="sm"
                          onClick={() => handleStartChat(match)}
                          className="w-full"
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          {t('chat')}
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}

            {/* Edit Settings */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSetup(true)}
              className="w-full"
            >
              {t('editPreferences')}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};
